import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Dropdown } from 'ui';
import { injectIntl } from 'react-intl';
import ErrorHandler from 'common/error/components/error-handler';

class ReferenceDataDropdown extends Component {
  static propTypes = {
    data: PropTypes.object,
    /**
     * Type, will be filled in in /api/refdata/lists/{serviceCenter}/{type}/{name} url
     */
    type: PropTypes.string,
    /**
     * Name, will be filled in in /api/refdata/lists/{serviceCenter}/{type}/{name} url
     */
    name: PropTypes.string,
    /**
     * ValueKey, identifier within the results array
     */
    valueKey: PropTypes.string,
    /**
     * ValueLabel, key to display within the results array
     */
    valueLabel: PropTypes.string,
    /**
     * Custom endpoint, if you want to do something different than
     * /api/refdata/lists/{serviceCenter}/{type}/{name}
     */
    endpoint: PropTypes.string,
    /**
     * ValueKey, what property holds the array of reference data.
     * default: items[name]
     */
    valueProperty: PropTypes.string,
    value: PropTypes.string,
    getReferenceData: PropTypes.func,
    onChange: PropTypes.func,
    onObjectChanged: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    errorState: PropTypes.object,
    loadingState: PropTypes.object,
    urlParams: PropTypes.object,
    subType: PropTypes.string,
    firstOption: PropTypes.string,
    allowClear: PropTypes.bool,
    hasError: PropTypes.bool,
    transformValues: PropTypes.func,
    isSorted: PropTypes.bool,
    isGrouped: PropTypes.bool,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      filter: PropTypes.func.isRequired,
    })),
    width: PropTypes.number,
    intl: PropTypes.object,
    serviceCenterId: PropTypes.string,
    isPlanningBoard: PropTypes.bool,
    setDefaultValue: PropTypes.func,
    notFoundLabel: PropTypes.string,
    useBodyAsParent: PropTypes.bool,
    fullScreen: PropTypes.bool,
    dropdownMatchSelectWidth: PropTypes.bool,
    listHeight: PropTypes.number,
  };

  state = {
    values: [],
  };

  componentWillMount() {
    const {
      data,
      loadingState,
      type,
      name,
      getReferenceData,
      urlParams = {},
      subType,
      onObjectChanged,
      valueKey,
      value,
      endpoint,
      valueProperty,
      serviceCenterId,
    } = this.props;

    const hasCachedReferenceData = data && data[`${serviceCenterId}_${type}_${name}`];
    const isLoading = loadingState[`${serviceCenterId}_${type}_${name}`] || false;

    // if no data is cached for this request & the same request is not loading,
    // fetch the data
    if (
      serviceCenterId && (
        (!hasCachedReferenceData && !isLoading) ||
        Object.keys(urlParams).length
      )
    ) {
      getReferenceData({
        endpoint,
        objectType: type,
        name,
        urlParams,
        hasSubTypes: !!subType,
        valueProperty,
        serviceCenterId,
      });
    }
    let values = [];
    if (subType) {
      values =
        hasCachedReferenceData && !Object.keys(urlParams).length
          ? data[`${serviceCenterId}_${type}_${name}`][subType]
          : [];
    } else {
      values =
        hasCachedReferenceData && !Object.keys(urlParams).length
          ? data[`${serviceCenterId}_${type}_${name}`]
          : [];
    }

    this.setState({
      values,
    });
    if (onObjectChanged && values && values.length) {
      onObjectChanged(values?.find(i => i[valueKey || 'id'] === value));
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      data,
      type,
      name,
      subType,
      onObjectChanged,
      valueKey,
      value,
      urlParams = {},
      serviceCenterId,
      getReferenceData,
      endpoint,
      valueProperty,
      setDefaultValue,
    } = nextProps;

    if (serviceCenterId !== this.props.serviceCenterId) {
      getReferenceData({
        endpoint,
        objectType: type,
        name,
        urlParams,
        hasSubTypes: !!subType,
        valueProperty,
        serviceCenterId,
      });
    }

    // if the data is not present, don't change state
    if (!data[`${serviceCenterId}_${type}_${name}`]) {
      return;
    }

    // if the data is the same,
    // and service center is also the same, don't change state
    if (this.props.data === data && serviceCenterId === this.props.serviceCenterId) {
      return;
    }

    // if the data is different,
    // but there is cached data already in the props & there are no urlparams,
    // don't change state
    if (this.props.data !== data) {
      if (this.props.data[`${serviceCenterId}_${type}_${name}`] && !Object.keys(urlParams).length) {
        return;
      }
    }

    // all other scenario's,
    // change state
    let values;
    if (subType) {
      values = data[`${serviceCenterId}_${type}_${name}`][subType];
    } else {
      values = data[`${serviceCenterId}_${type}_${name}`];
    }

    if (setDefaultValue && values?.length) {
      const defaultValue = values?.find(v => v.isDefault) || values[0];
      setDefaultValue(defaultValue[valueKey]);
    }

    this.setState({
      values,
    });
    if (onObjectChanged && values && values.length) {
      onObjectChanged(values?.find(i => i[valueKey || 'id'] === value));
    }
  }

  onChange = (id) => {
    const { onChange, onObjectChanged, valueKey } = this.props;
    if (onObjectChanged) {
      onObjectChanged(this.state.values?.find(i => i[valueKey || 'id'] === id));
    }
    return onChange ? onChange(id) : null;
  };

  getValue = () => {
    const { value, notFoundLabel, valueKey, intl } = this.props;
    const { values } = this.state;
    if (!notFoundLabel) {
      return values?.length
        ? value
        : intl.formatMessage({ id: 'common.placeholders.NO_DATA_FOUND' });
    }
    return values?.find(v => v[valueKey] === value) ? value : notFoundLabel;
  };

  render() {
    const {
      errorState,
      loadingState,
      placeholder,
      disabled = false,
      valueKey,
      valueLabel,
      type,
      name,
      firstOption,
      allowClear,
      hasError,
      transformValues,
      groups = [],
      isGrouped = false,
      isSorted = false,
      width,
      intl,
      serviceCenterId,
      isPlanningBoard = false,
      value,
      useBodyAsParent = false,
      fullScreen,
      dropdownMatchSelectWidth = false,
      listHeight = 256,
    } = this.props;

    let { values } = this.state;
    if (transformValues) {
      values = transformValues(values);
    }
    let sortedValues = values;
    if (firstOption) {
      sortedValues = values?.sort((a, b) => {
        if (a[valueKey] === firstOption) {
          return -1;
        }
        if (b[valueKey] === firstOption) {
          return 1;
        }
        if (isSorted) {
          return b[valueLabel] <= a[valueLabel] ? 1 : -1;
        }
        return 0;
      });
    }

    const isLoading = loadingState[`${serviceCenterId}_${type}_${name}`] || false;
    const error = errorState[`${serviceCenterId}_${type}_${name}`];
    const isDisabled = disabled ||
      isLoading ||
      !!(error && Object.keys(error).length) ||
      ((value && !allowClear) && sortedValues?.length === 1);

    /* eslint-disable no-undefined */
    return (
      <>
        <ErrorHandler
          type='notification'
          error={error}
          isFullScreen={isPlanningBoard}
        />
        <Dropdown
          width={width}
          groups={groups}
          isGrouped={isGrouped}
          values={sortedValues}
          placeholder={isLoading ? intl.formatMessage({ id: 'common.LOADING' }) : placeholder}
          onChange={this.onChange}
          disabled={isDisabled || !values?.length}
          valueKey={valueKey}
          valueLabel={valueLabel}
          value={this.getValue()}
          allowClear={allowClear || false}
          listHeight={listHeight}
          hasError={hasError}
          dropdownMatchSelectWidth={dropdownMatchSelectWidth}
          getPopupContainer={triggerNode => useBodyAsParent && !fullScreen ?
            document.body :
            triggerNode.parentNode
          }
        />
      </>
    );
    /* eslint-enable no-undefined */
  }
}

export default injectIntl(ReferenceDataDropdown, { forwardRef: true });
